/* Authenticated customer preview. No Admin token or private catalog is persisted. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else if (!root.KnowFitCustomerDraft) root.KnowFitCustomerDraft = factory(root);
})(typeof window !== 'undefined' ? window : globalThis, function createCustomerDraft(root) {
  'use strict';
  const STORAGE_KEY = 'knoweats:knowfit-customer-draft:v1';
  const ACCESS_TIMEOUT_MS = 3000;
  let state = 'idle', record = null, accessPromise = null, inFlight = null, privateSession = false;
  let generation = 0, checkedAt = 0, message = '', busy = false;
  function config() {
    try { return JSON.parse(root.document.querySelector('[data-knowfit-customer-draft-config]')?.textContent || '{}'); }
    catch { return {}; }
  }
  const text = key => config().messages?.[key] || key;
  function marked() {
    try { return root.sessionStorage.getItem(STORAGE_KEY) !== null; } catch { return true; }
  }
  function isActive() {
    return state !== 'live' && (privateSession || marked() || state === 'checking');
  }
  function endpoint() {
    const url = new URL(config().endpoint, root.location.origin);
    if (url.origin !== root.location.origin || url.username || url.password || url.search || url.hash
      || !/^\/(?:apps|a|tools|community)\/[^/]+\/?$/.test(url.pathname)) throw new Error(text('connection_error'));
    return url.href;
  }
  async function request(input, signal) {
    const response = await root.fetch(endpoint(), { method: 'POST', credentials: 'same-origin',
      cache: 'no-store', redirect: 'error', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input), signal });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(text(['LOGIN_REQUIRED', 'ACCESS_DENIED', 'PREVIEW_DISABLED'].includes(data.error) ? 'access_denied'
        : data.error === 'NO_COLLECTIONS' ? 'no_collections' : 'connection_error'));
      error.code = data.error;
      throw error;
    }
    return data;
  }
  async function checkAccess() {
    const controller = new AbortController();
    let timer;
    // A slow proxy must not hold the public plan indefinitely. Late replies have no side effects.
    try {
      return await Promise.race([
        request({ action: 'access' }, controller.signal),
        new Promise((_, reject) => {
          timer = (root.setTimeout || setTimeout)(() => {
            controller.abort();
            reject(new Error(text('connection_error')));
          }, ACCESS_TIMEOUT_MS);
        }),
      ]);
    } finally { (root.clearTimeout || clearTimeout)(timer); }
  }
  async function ready({ retry = false } = {}) {
    if (accessPromise && (!retry || state === 'checking')) return accessPromise;
    const version = generation;
    const work = (async () => {
      try {
        const cfg = config();
        if (!cfg.enabled || !cfg.customerId) {
          if (privateSession || marked()) throw new Error(text('access_denied'));
          state = 'live'; return false;
        }
        state = 'checking'; render();
        const data = await checkAccess();
        if (version !== generation) return false;
        if (!data.authorized) {
          if (privateSession || marked()) throw new Error(text('access_denied'));
          state = 'live'; return false;
        }
        if (String(data.customerId) !== String(cfg.customerId)) throw new Error(text('access_denied'));
        privateSession = true;
        root.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ customerId: String(data.customerId), shop: cfg.shop }));
        state = 'private'; message = text('active'); return true;
      } catch (error) {
        if (version !== generation) return false;
        record = null;
        if (!privateSession && !marked()) {
          // This page has never entered a private test: keep the public flow independent of the app.
          state = 'live'; message = ''; return false;
        }
        state = 'blocked'; message = error.message;
        throw error;
      } finally {
        render();
      }
    })();
    accessPromise = work;
    return work;
  }
  function getRecord() {
    if (state !== 'private' || !record) throw new Error(message || text('refresh_required'));
    return record;
  }
  function collections() {
    const catalog = JSON.parse(root.document.querySelector('[data-knowfit-catalog]')?.textContent || '{}');
    return Object.fromEntries(['breakfast', 'main', 'snack', 'dinner', 'extra'].map(type => {
      const source = catalog.sources?.[type];
      const id = source && (source.assigned ?? source.configured) && source.id ? String(source.id) : '';
      return [type, id ? id.startsWith('gid://') ? id : `gid://shopify/Collection/${id}` : null];
    }));
  }
  async function refresh({ signal, force = true } = {}) {
    await ready();
    if (state !== 'private') return null;
    if (!force && record && Date.now() - checkedAt < 1000) return record;
    if (inFlight) { await inFlight; if (signal?.aborted) throw new DOMException('Aborted', 'AbortError'); return getRecord(); }
    const version = generation;
    const work = (async () => {
      const data = await request({ action: 'catalog', collections: collections() }, signal);
      if (signal?.aborted || version !== generation) throw new DOMException('Aborted', 'AbortError');
      if (String(data.customerId) !== String(config().customerId)) throw new Error(text('access_denied'));
      record = root.KnowFitTestCatalog.convertAdminExports(data.exports);
      checkedAt = Date.now();
      message = `${text('updated')} ${new Date(checkedAt).toLocaleTimeString()} · ${record.summary.product_count} ${text('products')} · ${record.summary.valid_variant_count} ${text('variants')}`;
      render(); return record;
    })();
    inFlight = work;
    try { return await work; }
    catch (error) {
      if (version === generation) { record = null; checkedAt = 0; message = error.message; render(); }
      throw error;
    } finally { if (inFlight === work) inFlight = null; }
  }
  function beforeChange() {
    if (!root.dispatchEvent(new root.CustomEvent('knowfit:before-test-catalog-change', { cancelable: true }))) {
      throw new Error(text('operation_pending'));
    }
  }
  const changed = () => root.dispatchEvent(new root.CustomEvent('knowfit:test-catalog-change'));
  async function update() {
    if (busy) return;
    beforeChange(); busy = true; render();
    try { await ready({ retry: true }); await refresh(); changed(); }
    catch (error) { message = error.message; }
    finally { busy = false; render(); }
  }
  function usePublic() {
    beforeChange(); ++generation;
    root.sessionStorage.removeItem(STORAGE_KEY);
    record = null; inFlight = null; state = 'live'; busy = false; privateSession = false; accessPromise = Promise.resolve(false);
    changed(); render();
  }
  function render() {
    root.document.querySelectorAll('[data-knowfit-customer-draft-controls]').forEach(panel => {
      panel.hidden = !(privateSession || marked());
      panel.querySelector('[data-customer-draft-refresh]').disabled = busy || state === 'checking';
      panel.querySelector('[data-customer-draft-status]').textContent = message || text(state === 'checking' ? 'checking' : 'refresh_required');
      panel.querySelector('[data-customer-draft-warnings]').textContent = record?.summary?.warnings?.join(' ') || '';
    });
  }
  function initialize() {
    root.document.querySelectorAll('[data-knowfit-customer-draft-controls]').forEach(panel => {
      if (panel.dataset.initialized) return;
      panel.dataset.initialized = 'true';
      for (const [name, callback] of [['refresh', update], ['exit', usePublic]]) {
        panel.querySelector(`[data-customer-draft-${name}]`).addEventListener('click', () => {
          Promise.resolve().then(callback).catch(error => { message = error.message; render(); });
        });
      }
    });
    // Merely including the global modal or reloading a theme section must never call the proxy.
    render();
  }
  root.document.addEventListener?.('shopify:section:load', initialize);
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  return Object.freeze({ STORAGE_KEY, isActive, ready, refresh, getRecord, update, usePublic });
});
